import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Divider,
    useTheme,
    Alert,
    Snackbar
} from '@mui/material';
import { tokens } from '../../../theme';
import Header from '../../../components/Header';
import Loading from '../../../components/Loading';
import QuestionReviewPanel from './components/QuestionReviewPanel';
import ReviewProgressBar from './components/ReviewProgressBar';
import QuestionNavigator from './components/QuestionNavigator';
import ReviewSubmissionModal from './components/ReviewSubmissionModal';
import useReviewSession from './hooks/useReviewSession';
import useQuestionNavigation from './hooks/useQuestionNavigation';
import useAutoSave from './hooks/useAutoSave';
import { submitReview, updateReview } from './utils/reviewApi';
import { useSignOut } from "react-auth-kit";

const TeacherReviewPage = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const signOut = useSignOut();
    
    // State management
    const [reviews, setReviews] = useState({});
    const [currentReview, setCurrentReview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [submissionModalOpen, setSubmissionModalOpen] = useState(false);
    const [filterStatus, setFilterStatus] = useState('all');
    const [timeTracking, setTimeTracking] = useState({});

    // Custom hooks
    const {
        session,
        questions,
        loading: sessionLoading,
        error: sessionError,
        refetchProgress
    } = useReviewSession(sessionId);

    const {
        currentQuestionIndex,
        goToNext,
        goToPrevious,
        goToQuestion,
        canGoNext,
        canGoPrevious
    } = useQuestionNavigation(questions?.length || 0);

    // Auto-save hook - now includes time tracking
    const { saveToLocal, loadFromLocal } = useAutoSave(sessionId, reviews, timeTracking);

    // Divider handling for split pane
    const [dividerPosition, setDividerPosition] = useState(7);
    const rightPaneRef = useRef(null);

    const handleMouseDown = () => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e) => {
        if (rightPaneRef.current) {
            const newDividerPosition = (e.clientX / window.innerWidth) * 10;
            setDividerPosition(Math.min(Math.max(newDividerPosition, 3), 8));
        }
    };

    const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };

    // Seed reviews + time tracking once questions load. Start from the reviews already
    // persisted on the backend — each question carries the current teacher's `my_review` —
    // then overlay any local draft so unsaved in-progress edits are restored on top.
    // Without the backend seed, returning on another device / after a cache clear / >24h
    // later / after a prior "Submit All" would show every question as "Not Reviewed" and
    // 0% progress even though the reviews exist on the server (only the form was restoring).
    useEffect(() => {
        if (!questions) return;

        const backendReviews = {};
        const backendTime = {};
        questions.forEach((q) => {
            if (q.my_review) {
                backendReviews[q.item_id] = q.my_review;
                if (q.my_review.time_spent_seconds != null) {
                    backendTime[q.item_id] = q.my_review.time_spent_seconds;
                }
            }
        });

        const savedData = loadFromLocal();

        // Reviews: backend base, local draft (newer, possibly-unsaved edits) on top,
        // anything already edited this session (prev) preserved so we never clobber input.
        setReviews((prev) => ({ ...backendReviews, ...(savedData?.reviews || {}), ...prev }));

        // Time spent is cumulative and only ever grows, so take the max across the backend
        // total, any local draft, and the live counter. This keeps the absolute value the
        // server persists correct on any device (a stale draft can't lower the total) — the
        // counter must be seeded from the backend or a fresh device would overwrite the
        // saved time with just this session's seconds.
        setTimeTracking((prev) => {
            const merged = { ...prev };
            [backendTime, savedData?.timeTracking].forEach((src) => {
                Object.keys(src || {}).forEach((k) => {
                    merged[k] = Math.max(merged[k] || 0, src[k] || 0);
                });
            });
            return merged;
        });
    }, [questions]);

    // Track time spent on question using setInterval
    useEffect(() => {
        if (questions && questions[currentQuestionIndex]) {
            const currentQuestion = questions[currentQuestionIndex];
            const questionId = currentQuestion.item_id; // Capture ID at the start
            
            // Increment time every second
            const interval = setInterval(() => {
                setTimeTracking(prev => ({
                    ...prev,
                    [questionId]: (prev[questionId] || 0) + 1  // Increment by 1 second
                }));
            }, 1000); // Run every 1000ms (1 second)
            
            // Cleanup: clear the interval when component unmounts or question changes
            return () => {
                clearInterval(interval);
            };
        }
    }, [currentQuestionIndex, questions]);

    // Load current question's review
    useEffect(() => {
        if (questions && questions[currentQuestionIndex]) {
            const question = questions[currentQuestionIndex];
            const existingReview = reviews[question.item_id] || question.my_review || null;
            setCurrentReview(existingReview);
        }
    }, [currentQuestionIndex, questions, reviews]);

    // Handle review save
    const handleSaveReview = async (reviewData) => {
        if (!questions || !questions[currentQuestionIndex]) return;
        
        const question = questions[currentQuestionIndex];
        const timeSpent = timeTracking[question.item_id] || 0;
        
        // Ensure item_id is a valid integer
        const itemId = parseInt(question.item_id, 10);
        if (isNaN(itemId)) {
            setError('Invalid question ID. Please refresh the page and try again.');
            return;
        }
        
        setSaving(true);
        setError(null);
        
        try {
            const reviewPayload = {
                review_item_id: itemId,
                ...reviewData,
                time_spent_seconds: timeSpent
            };

            let response;
            if (currentReview?.id) {
                response = await updateReview(currentReview.id, reviewPayload);
            } else {
                response = await submitReview(reviewPayload);
            }

            // Update local state
            const updatedReview = {
                ...reviewPayload,
                id: response.id,
                reviewed_at: new Date().toISOString()
            };
            
            setReviews(prev => ({
                ...prev,
                [question.item_id]: updatedReview
            }));
            
            setCurrentReview(updatedReview);
            saveToLocal({ 
                reviews: { ...reviews, [question.item_id]: updatedReview },
                timeTracking 
            });
            
            setSuccess('Review saved successfully');
            
            // Refresh progress
            refetchProgress();
            
        } catch (err) {
            console.error('Error saving review:', err);
            setError(err.message || 'Failed to save review');
            
            if (err.status === 401) {
                signOut();
                navigate('/signin');
            }
        } finally {
            setSaving(false);
        }
    };

    // Handle submission
    const handleSubmitAll = async () => {
        // Check if all required questions are reviewed
        const requiredQuestions = questions.filter(q => q.is_required);
        const unreviewed = requiredQuestions.filter(q => !reviews[q.item_id]);
        
        if (unreviewed.length > 0) {
            setError(`Please review all required questions. ${unreviewed.length} required questions remaining.`);
            return;
        }
        
        setSubmissionModalOpen(true);
    };

    const confirmSubmission = async () => {
        setSubmissionModalOpen(false);
        
        // Clear local storage
        localStorage.removeItem(`teacherReview_${sessionId}`);
        
        // Navigate back to sessions list
        navigate('/classrooms/classes');
        setSuccess('All reviews submitted successfully!');
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyPress = (e) => {
            // Don't trigger if user is typing in textarea/input
            if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') {
                return;
            }
            
            if (e.key === 'ArrowRight' || e.key === 'n') {
                if (canGoNext) goToNext();
            } else if (e.key === 'ArrowLeft' || e.key === 'p') {
                if (canGoPrevious) goToPrevious();
            } else if (e.ctrlKey && e.key === 'Enter') {
                handleSubmitAll();
            }
        };
        
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [canGoNext, canGoPrevious, goToNext, goToPrevious]);

    // Error handling for session
    if (sessionError) {
        if (sessionError.includes('403')) {
            return (
                <Box m={3}>
                    <Alert severity="error">
                        You are not authorized to access this review session.
                    </Alert>
                </Box>
            );
        }
        if (sessionError.includes('404')) {
            return (
                <Box m={3}>
                    <Alert severity="error">
                        Review session not found.
                    </Alert>
                </Box>
            );
        }
    }

    if (sessionLoading) {
        return (
            <Box
                sx={{
                    height: '100vh',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Loading />
            </Box>
        );
    }

    if (!session || !questions || questions.length === 0) {
        return (
            <Box m={3}>
                <Alert severity="warning">
                    No questions available for review in this session.
                </Alert>
            </Box>
        );
    }

    const currentQuestion = questions[currentQuestionIndex];
    const progress = {
        total: questions.length,
        reviewed: Object.keys(reviews).length,
        percentage: Math.round((Object.keys(reviews).length / questions.length) * 100)
    };

    return (
        <Box
            sx={{
                // This route renders directly inside `.app` (which is `overflow: hidden`),
                // unlike layout pages that live inside the scrollable `.content` wrapper.
                // So this page must own its own vertical scroll. Scoped to this page only —
                // do NOT move this into the global CSS (would affect every page).
                flex: 1,
                height: '100dvh',
                overflowY: 'auto',
                px: '20px',
                py: 3,
            }}
        >
            <Header
                title={session.title || "Teacher Review"}
                subtitle={session.instructions || "Review questions and provide feedback"}
            />
            
            <Box maxWidth={1400} mx="auto" pb={10}>
                <ReviewProgressBar progress={progress} />
                
                <Box display="flex" minHeight="80vh" mt={2}>
                    {/* Left Panel - Question Review */}
                    <Box flex={dividerPosition} pr={2}>
                        <QuestionReviewPanel
                            question={currentQuestion}
                            questionNumber={currentQuestionIndex + 1}
                            totalQuestions={questions.length}
                            review={currentReview}
                            onSaveReview={handleSaveReview}
                            onNext={canGoNext ? goToNext : null}
                            onPrevious={canGoPrevious ? goToPrevious : null}
                            saving={saving}
                        />
                    </Box>

                    {/* Divider */}
                    <Divider
                        orientation="vertical"
                        sx={{
                            cursor: 'col-resize',
                            '&:hover': {
                                backgroundColor: colors.primary[300],
                                width: '3px'
                            }
                        }}
                        onMouseDown={handleMouseDown}
                    />

                    {/* Right Panel - Navigation */}
                    <Box 
                        ref={rightPaneRef}
                        flex={10 - dividerPosition} 
                        pl={2}
                        sx={{
                            position: 'sticky',
                            top: 20,
                            height: '80vh',
                            alignSelf: 'flex-start'
                        }}
                    >
                        <QuestionNavigator
                            questions={questions}
                            currentIndex={currentQuestionIndex}
                            reviews={reviews}
                            onQuestionSelect={goToQuestion}
                            filterStatus={filterStatus}
                            onFilterChange={setFilterStatus}
                            onSubmitAll={handleSubmitAll}
                            canSubmit={session.require_all_reviews ? 
                                Object.keys(reviews).length === questions.length :
                                Object.keys(reviews).length > 0
                            }
                        />
                    </Box>
                </Box>
            </Box>

            {/* Submission Modal */}
            <ReviewSubmissionModal
                open={submissionModalOpen}
                onClose={() => setSubmissionModalOpen(false)}
                onConfirm={confirmSubmission}
                reviewCount={Object.keys(reviews).length}
                totalQuestions={questions.length}
                session={session}
            />

            {/* Success/Error Snackbars */}
            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
            >
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            </Snackbar>
            
            <Snackbar
                open={!!success}
                autoHideDuration={3000}
                onClose={() => setSuccess(null)}
            >
                <Alert severity="success" onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default TeacherReviewPage;